import React, {Fragment, useState} from 'react';
import { FormattedMessage } from 'react-intl';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { Component } from '../../../infrastructure/type-extensions';
import './make-multi-select.scss';
import { DefaultClosePopoverButton } from '../common-popover/default-close-popover-button';
import classnames from 'classnames';
import {MultiSelectOption} from '../multi-select-list/_multi-select-option';
import { AttendanceType, CustomFieldsOptionsListDTO, RestrictAccessFilter } from '../../../api/custom-pages.dao';
import { noneAsNullOrUndefined } from '../../_events/_manage-event/_edit-website/website-form-body/passport-pages/default-pages/restrict-access-container';

interface IMultiSelectClosedPropsDerivedFromItemsList {
    isOpen: boolean;
}

const makeFiltersMultiSelect = <T extends IMultiSelectClosedPropsDerivedFromItemsList>(ComponentWhichTriggerSingleSelection: Component<T>) => {
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <ComponentWhichTriggerSingleSelection {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });

    return props => {
        const {
            items,
            applyButtonText,
            resetButtonText,
            onChange,
            position,
            align
        } = props;

        const [selectedValues, setSelectedValues] = useState<RestrictAccessFilter>({
            allowedOrganizationTypes: [],
            allowedAttendanceMethods: [],
            allowedOptions: {}
        });

        const {allowedOptions = {}, ...restFilters} = items;

        const onFilterToggle = (type: string, filter: string, index: number) => {
            if (type === 'allowedOrganizationTypes' && index === 0) {
                filter = noneAsNullOrUndefined;
            }
            if (selectedValues[type].includes(filter)) {
                selectedValues[type] = selectedValues[type].filter(el => el !== filter);
            } else {
                selectedValues[type].push(filter);
            }
            setSelectedValues({...selectedValues});
        };

        const onAllowedOptionsFilterToggle = (customFieldId: string, filter: string) => {
            if (selectedValues.allowedOptions[customFieldId]?.includes(filter)) {
                selectedValues.allowedOptions[customFieldId] = selectedValues.allowedOptions[customFieldId].filter(el => el !== filter);
            } else {
                selectedValues.allowedOptions[customFieldId] =
                    selectedValues.allowedOptions[customFieldId]?.length
                    ? [...selectedValues.allowedOptions[customFieldId], filter]
                    : [filter]
                ;
            }
            setSelectedValues({...selectedValues});
        };

        const resetToDefault = () => {
            setSelectedValues({
                allowedOrganizationTypes: [],
                allowedAttendanceMethods: [],
                allowedOptions: {}
            });
        };

        const handleSave = () => {
            onChange(selectedValues);
            resetToDefault();
        };

        const filterTypesToTextMapping = (type: string) => {
            switch (type) {
                case 'allowedOrganizationTypes':
                    return (
                        <FormattedMessage
                            id='oe.defaultPage.organizationType'
                            defaultMessage='Organization Type'
                            description='Label text'
                        />
                    );
                case 'allowedAttendanceMethods':
                    return (
                        <FormattedMessage
                            id='oe.defaultPage.attendanceType'
                            defaultMessage='Attendance Method'
                            description='Label text'
                        />
                    );
                default:
                    return type;
            }
        };

        const attendanceTypesToTextMapping = (type: string) => {
            switch (type) {
                case AttendanceType.InPerson:
                    return (
                        <FormattedMessage
                            id='oe.common.inPerson'
                            defaultMessage='In Person'
                            description='Label text'
                        />
                    );
                case AttendanceType.Virtual:
                    return (
                        <FormattedMessage
                            id='oe.common.virtual'
                            defaultMessage='Virtual'
                            description='Label text'
                        />
                    );
                case AttendanceType.Both:
                    return (
                        <FormattedMessage
                            id='oe.common.both'
                            defaultMessage='Both'
                            description='Label text'
                        />
                    );
                default:
                    return type;
            }
        };

        const isFilterSelected = (type: string, filter: string) => {
            return selectedValues[type].includes(filter);
        };

        const isAllowedOptionsFilterSelected = (customFieldId: string, filter: string) => {
            return selectedValues.allowedOptions[customFieldId]?.includes(filter);
        };

        return (
            <CommonPopover
                body={(
                    <>
                        <div className={classnames('multi-select-list', 'multi-select-list--filters')}>
                            {Object.entries(restFilters).map(([type, filters]) => {
                                const filterTypeText = filterTypesToTextMapping(type);
                                const filterNames = filters as string[];

                                return (
                                    <Fragment key={type}>
                                        <div className={classnames('multi-select-list__option',
                                            type,
                                            'multi-select-list__option--disabled')}>
                                            <div className={classnames(
                                                'multi-select-list__option-text',
                                                'multi-select-list__option-text--group'
                                            )}>
                                                {filterTypeText}
                                            </div>
                                        </div>
                                        {filterNames.length ? filterNames.map((filter, index) => {
                                            let filterName = attendanceTypesToTextMapping(filter);
                                            const isSelected = isFilterSelected(type, filter);
                                            const shouldSetFilterNameAsNone = type === 'allowedOrganizationTypes'
                                                && filterName === noneAsNullOrUndefined
                                                && index === 0
                                            ;
                                            if (shouldSetFilterNameAsNone) {
                                                filterName = 'None';
                                            }

                                            return (
                                                <MultiSelectOption
                                                    key={filter + index}
                                                    isSelected={isSelected}
                                                    isInsideGroup={true}
                                                    onClick={() => onFilterToggle(type, filter, index)}
                                                    name={filterName}
                                                />
                                            );
                                        }) : <div className={classnames('multi-select-list__option',
                                            'multi-select-list__option--disabled')}>
                                            <div className={classnames(
                                                'multi-select-list__option-text',
                                                'multi-select-list__option-text--group',
                                                'multi-select-list__option-text--within-group',
                                                'overflow-default'
                                            )}>
                                                <FormattedMessage
                                                    id='oe.common.noOrganizationTypesAvailable'
                                                    defaultMessage='No Organization Types available'
                                                    description='Label text'/>
                                            </div>
                                        </div>
                                        }
                                    </Fragment>
                                );
                            })}

                            {!!Object.keys(allowedOptions).length && Object.values(allowedOptions as CustomFieldsOptionsListDTO[]).map(optionField => (
                                <Fragment key={optionField.customFieldId}>
                                    <div className={classnames('multi-select-list__option',
                                        'multi-select-list__option--disabled')}>
                                        <div className={classnames(
                                            'multi-select-list__option-text',
                                            'multi-select-list__option-text--group'
                                        )}>
                                            {optionField.label}
                                        </div>
                                    </div>
                                    {optionField.optionsList.map(option => {
                                            const isSelected = isAllowedOptionsFilterSelected(optionField.customFieldId, option);

                                            return (
                                                <MultiSelectOption
                                                    key={option}
                                                    isSelected={isSelected}
                                                    isInsideGroup={true}
                                                    onClick={() => onAllowedOptionsFilterToggle(optionField.customFieldId, option)}
                                                    name={option}
                                                />
                                            );
                                        })}

                                </Fragment>
                            ))

                            }
                        </div>
                    </>
                )}
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultClosePopoverButton
                            text={applyButtonText}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => handleSave()}
                        />
                        <DefaultClosePopoverButton
                            onClick={() => resetToDefault()}
                            text={resetButtonText}
                        />
                    </div>
                }
                position={position || 'left'}
                align={align || 'start'}
                onClickOutside={resetToDefault}
            >
                <ComponentWithForwardedRef {...props} />
            </CommonPopover>
        );
    };
};

export { makeFiltersMultiSelect, IMultiSelectClosedPropsDerivedFromItemsList };
